import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShoppingCart, PackageOpen } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { resolveImg } from '../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  parts: 'AN Fittings & Parts',
  hats: 'Hats',
  shirts: 'Shirts',
  other: 'More Gear',
};

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = (product) => {
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  const grouped = products.reduce((acc, p) => {
    const key = CATEGORY_LABELS[p.category] ? p.category : 'other';
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});

  return (
    <section id="shop" className="py-20 px-4 bg-white/5 border-y border-gold/10">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Shop <span className="text-gold">Parts & Gear</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            AN fittings, OLD2NEW hats, shirts and more — shipped straight to your garage.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-white/50">Loading shop…</p>
        ) : products.length === 0 ? (
          <div className="text-center text-white/50 py-12" data-testid="shop-empty">
            <PackageOpen className="w-12 h-12 mx-auto mb-4 opacity-40" />
            New products are coming soon. Check back shortly!
          </div>
        ) : (
          <div className="space-y-12" data-testid="shop-products">
            {Object.keys(grouped).map((cat) => (
              <div key={cat}>
                <h3 className="text-2xl font-bold text-white mb-6">{CATEGORY_LABELS[cat]}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {grouped[cat].map((p) => (
                    <Card
                      key={p.id}
                      className="bg-black border-white/10 overflow-hidden hover:border-gold/50 transition-colors flex flex-col"
                      data-testid={`product-card-${p.id}`}
                    >
                      <div className="aspect-square bg-white/5 relative">
                        <img src={resolveImg(p.image)} alt={p.name} className="w-full h-full object-cover" />
                        {!p.in_stock && (
                          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                            <Badge className="bg-white/10 text-white border-white/20">Sold Out</Badge>
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4 flex flex-col flex-1">
                        <h4 className="font-semibold text-white">{p.name}</h4>
                        {p.description && (
                          <p className="text-white/50 text-sm mt-1 line-clamp-2">{p.description}</p>
                        )}
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          <span className="text-gold font-bold text-lg">${p.price.toFixed(2)}</span>
                          <Button
                            size="sm"
                            disabled={!p.in_stock}
                            onClick={() => handleAdd(p)}
                            className="bg-gold text-black hover:bg-gold/90 font-semibold disabled:opacity-40"
                            data-testid={`add-to-cart-${p.id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Shop;
